import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil, take } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { faSearch, faTimes, faSignal, faDatabase, faCogs, faCircle, faEdit, faSave, faCheck, faChevronRight, faChevronDown, faDesktop, faBuilding, faWrench, faQuestionCircle, faEye, faRuler, faInfoCircle, faMicrochip } from '@fortawesome/free-solid-svg-icons';
import { MultiLangField, setMultiLanguageField } from '../../customization/multilang-input/multi-language';
import { LoggerService } from 'src/app/core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { AppStateInterface } from 'src/app/appState.interface';
import { getApplySelectedCustomer } from 'src/app/core/store/selectors';
import { CatalogService } from 'src/app/core/services/catalog.service';
import { CatalogResponseV2 } from 'src/app/core/models/catalog.model';
import { normalizeScope, getOriginalScopeKeys } from 'src/app/core/config/config.constants';
import { UtilsService } from 'src/app/core/services/utils.service';

interface SignalAttribute {
  name: string;
  label: string;
  type: string;
  unit: string | null;
  scope: string;
  source: 'common' | 'model';
  availableInModels?: string[];
  visible?: boolean;
  kpi?: boolean;
  i18nLabel?: { [key: string]: string };
  description?: { [key: string]: string };
  isNew?: boolean;
  uid?: string;
  function?: string | null;
  canMaps?: string[];
}

@Component({
  selector: 'app-signals-catalog20',
  templateUrl: './signals-catalog20.component.html',
  styleUrls: ['./signals-catalog20.component.scss']
})
export class SignalsCatalog20Component implements OnInit, OnDestroy, OnChanges {
  /** Scope esclusi dal catalogo segnali (gestiti nel tab Work Data) */
  private static readonly EXCLUDED_SCOPES: string[] = ['kpi_custom'];

  private destroy$ = new Subject<void>();
  private catalogResponse: CatalogResponseV2 | null = null;

  get configurationUid(): string | null { return this.catalogResponse?.uidConfiguration ?? null; }
  get inheritedFromCustomerUid(): string | null { return this.catalogResponse?.createdByCustomerUid ?? null; }

  // Icons
  faSearch = faSearch;
  faTimes = faTimes;
  faSignal = faSignal;
  faDatabase = faDatabase;
  faCogs = faCogs;
  faCircle = faCircle;
  faEdit = faEdit;
  faSave = faSave;
  faCheck = faCheck;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faDesktop = faDesktop;
  faBuilding = faBuilding;
  faWrench = faWrench;
  faQuestionCircle = faQuestionCircle;
  faEye = faEye;
  faRuler = faRuler;
  faInfoCircle = faInfoCircle;
  faMicrochip = faMicrochip;

  // Data
  commonAttributes: SignalAttribute[] = [];
  modelAttributes: Map<string, SignalAttribute[]> = new Map();
  filteredCommonAttributes: SignalAttribute[] = [];
  filteredModelAttributes: Map<string, SignalAttribute[]> = new Map();

  // Attributi raggruppati per scope (per accordion)
  filteredAttributesByScope: { [scope: string]: SignalAttribute[] } = {};

  // Loading & Error states
  isLoading = false;
  errorMessage: string | null = null;
  hasGlobalResults = true;

  // Search
  searchQuery = '';

  // Filters
  selectedScope = '';
  availableScopes: string[] = [];

  // Sorting
  sortColumn: 'name' | 'label' | 'unit' | 'visible' | 'kpi' = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Column filters per scope (ogni accordion ha il suo stato)
  colFilters: { [scope: string]: { name: string; label: string; visible: string; kpi: string } } = {};


  // Inline editing
  editingKey: string | null = null; // formato: "name|source"
  editingLabelField: MultiLangField | null = null;
  isSaving = false;

  // Stati accordion per i gruppi scope
  expandedGroups: { [scope: string]: boolean } = {};

  // Ordine degli scope (per eventuale drag&drop futuro)
  scopeOrder: string[] = [];

  // Modifiche pending da salvare (key = fieldName) - salviamo l'intera struttura del segnale
  private pendingChanges: Map<string, SignalAttribute> = new Map();
  /** Snapshot dei valori come caricati dal backend, per capire se una modifica riporta allo stato salvato */
  private originalAttributes: Map<string, SignalAttribute> = new Map();

  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';
  @Input() customerId: string | null = null;

  stylesheetString: string = 'aft';

  constructor(
    private logger: LoggerService,
    private translateService: TranslateService,
    private store: Store<AppStateInterface>,
    private catalogService: CatalogService,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
    console.log('📊 [SignalsCatalog20] ngOnInit, customerId input:', this.customerId);
    if (this.customerId) {
      this.loadCatalogForCustomer(this.customerId);
    } else {
      this.store.pipe(select(getApplySelectedCustomer), take(1)).subscribe(customer => {
        if (customer?.uid) {
          this.loadCatalogForCustomer(customer.uid);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && !changes['customerId'].firstChange && changes['customerId'].currentValue) {
      this.loadCatalogForCustomer(changes['customerId'].currentValue);
    }
  }

  reload(): void {
    if (this.customerId) {
      this.loadCatalogForCustomer(this.customerId);
    }
  }

  private loadCatalogForCustomer(customerId: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.catalogService.getCatalogForCustomer(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.catalogResponse = catalog;
          this.processCatalogData(catalog);
        },
        error: (err) => {
          console.error('❌ [SignalsCatalog20] Errore caricamento catalogo:', err);
          this.errorMessage = this.translateService.instant('signalsCatalog.catalogLoadError');
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processCatalogData(catalog: CatalogResponseV2): void {
    if (!catalog?.data || !Array.isArray(catalog.data)) {
      this.errorMessage = this.translateService.instant('signalsCatalog.noDataAvailable');
      return;
    }
    this.isLoading = true;
    this.errorMessage = null;
    this.mergeCatalogWithSavedConfig(catalog);
  }

  private mergeCatalogWithSavedConfig(catalog: CatalogResponseV2): void {
    this.commonAttributes = [];
    this.modelAttributes.clear();
    this.originalAttributes.clear();

    catalog.data.forEach((signal) => {
      const attribute: SignalAttribute = {
        name: signal.name,
        label: signal.label || signal.name,
        type: signal.type || 'unknown',
        unit: signal.unit ?? null,
        scope: signal.scope || 'common',
        source: signal.source as 'common' | 'model',
        uid: signal.uid ?? undefined,
        visible: signal.visible ?? true,
        kpi: signal.kpi ?? false,
        isNew: signal.isNew ?? false,
        i18nLabel: signal.i18nLabel ?? undefined,
        description: signal.description ?? undefined,
        ...(signal.function ? { function: signal.function } : {}),
        ...(signal.canMaps?.length ? { canMaps: signal.canMaps } : {}),
      };
      this.commonAttributes.push(attribute);
      this.originalAttributes.set(this.pendingKey(attribute), {
        ...attribute,
        i18nLabel: attribute.i18nLabel ? { ...attribute.i18nLabel } : undefined
      });
    });

    this.extractAvailableScopes();
    this.applyFilters();
    this.isLoading = false;
  }

  /**
   * Estrae gli scope disponibili dai dati
   */
  private extractAvailableScopes(): void {
    const scopesSet = new Set<string>();

    this.commonAttributes.forEach(attr => {
      if (attr.scope) scopesSet.add(normalizeScope(attr.scope));
    });

    this.modelAttributes.forEach(attrs => {
      attrs.forEach(attr => {
        if (attr.scope) scopesSet.add(normalizeScope(attr.scope));
      });
    });

    this.availableScopes = Array.from(scopesSet)
      .filter(s => !SignalsCatalog20Component.EXCLUDED_SCOPES.includes(s))
      .sort();
    console.log('📋 [SignalsCatalog20] Scope disponibili:', this.availableScopes);
  }

  /**
   * Applica i filtri ai dati e raggruppa per scope
   */
  applyFilters(): void {
    const query = this.searchQuery.toLowerCase().trim();
    const scopeFilter = this.selectedScope;

    // Step 1: global search + scope selector
    const allAttrs: SignalAttribute[] = [
      ...this.commonAttributes,
      ...Array.from(this.modelAttributes.values()).reduce((acc, a) => acc.concat(a), [] as SignalAttribute[])
    ];

    const globalFiltered = allAttrs.filter(attr => {
      if (query && !attr.name.toLowerCase().includes(query) &&
          !this.labelMatches(attr, query) &&
          !(attr.type && attr.type.toLowerCase().includes(query))) return false;
      if (scopeFilter && attr.scope !== scopeFilter) return false;
      return true;
    });

    // Step 2: raggruppa per scope normalizzato
    const byScope: { [scope: string]: SignalAttribute[] } = {};
    globalFiltered.forEach(attr => {
      const originalScope = attr.scope || 'other';
      if (SignalsCatalog20Component.EXCLUDED_SCOPES.includes(originalScope)) return;
      const normalizedScope = normalizeScope(originalScope);
      if (!byScope[normalizedScope]) byScope[normalizedScope] = [];
      byScope[normalizedScope].push(attr);
    });

    // Step 3: per ogni scope applica i filtri colonna locali + sort
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredAttributesByScope = {};

    Object.keys(byScope).forEach(scope => {
      const cf = this.getColFilter(scope);
      const filtered = byScope[scope].filter(attr => {
        if (cf.name && !attr.name.toLowerCase().includes(cf.name.toLowerCase())) return false;
        if (cf.label && !this.labelMatches(attr, cf.label.toLowerCase().trim())) return false;
        if (cf.visible === 'true' && attr.visible === false) return false;
        if (cf.visible === 'false' && attr.visible !== false) return false;
        if (cf.kpi === 'true' && !attr.kpi) return false;
        if (cf.kpi === 'false' && attr.kpi) return false;
        return true;
      });

      filtered.sort((a, b) => {
        switch (this.sortColumn) {
          case 'label':   return (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }) * dir;
          case 'unit':    return (a.unit || '').localeCompare(b.unit || '', undefined, { sensitivity: 'base' }) * dir;
          case 'visible': return ((b.visible ? 1 : 0) - (a.visible ? 1 : 0)) * dir;
          case 'kpi':     return ((b.kpi ? 1 : 0) - (a.kpi ? 1 : 0)) * dir;
          default:        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }) * dir;
        }
      });

      this.filteredAttributesByScope[scope] = filtered;
    });

    this.scopeOrder = Object.keys(this.filteredAttributesByScope).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    // true solo se il filtro globale ha trovato qualcosa (indipendente dai col-filter)
    this.hasGlobalResults = this.scopeOrder.length > 0;
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  onScopeFilterChange(): void {
    this.applyFilters();
  }

  setSortColumn(col: 'name' | 'label' | 'unit' | 'visible' | 'kpi'): void {
    if (this.sortColumn === col) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = col;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  getSortIcon(col: string): string {
    if (this.sortColumn !== col) return '↕';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  getColFilter(scope: string): { name: string; label: string; visible: string; kpi: string } {
    if (!this.colFilters[scope]) {
      this.colFilters[scope] = { name: '', label: '', visible: '', kpi: '' };
    }
    return this.colFilters[scope];
  }

  onColFilterChange(): void {
    this.applyFilters();
  }

  setColFilterBool(scope: string, field: 'visible' | 'kpi', value: string): void {
    this.getColFilter(scope)[field] = value;
    this.applyFilters();
  }

  hasActiveColFilters(scope: string): boolean {
    const cf = this.getColFilter(scope);
    return !!(cf.name || cf.label || cf.visible || cf.kpi);
  }

  clearColFilters(scope: string): void {
    this.colFilters[scope] = { name: '', label: '', visible: '', kpi: '' };
    this.applyFilters();
  }

  getScopeIcon(scope: string): any {
    const iconMap: { [key: string]: any } = {
      'signal': this.faSignal,
      'tcu': this.faCogs,
      'platform': this.faDesktop,
      'erp': this.faBuilding,
      'common': this.faWrench,
      'model': this.faCogs,
      'kpi': this.faCogs,
      'kpi_custom': this.faCogs,
      'other': this.faQuestionCircle,
      'general': this.faQuestionCircle
    };
    return iconMap[scope] || this.faQuestionCircle;
  }

  getScopeClass(scope: string): string {
    return `scope-${scope}`;
  }

  toggleGroup(scope: string): void {
    this.expandedGroups[scope] = !this.expandedGroups[scope];
  }

  isGroupExpanded(scope: string): boolean {
    return this.expandedGroups[scope] === true;
  }

  trackByScope(_index: number, scope: string): string {
    return scope;
  }

  getTotalSignalsCount(): number {
    return Object.values(this.filteredAttributesByScope).reduce((total, attrs) => total + attrs.length, 0);
  }

  getNewSignalsCount(scope: string): number {
    if (!this.filteredAttributesByScope[scope]) return 0;
    return this.filteredAttributesByScope[scope].filter(attr => attr.isNew === true).length;
  }

  getVisibleCount(scope: string): number {
    return (this.filteredAttributesByScope[scope] ?? []).filter(attr => attr.visible !== false).length;
  }

  getKpiCount(scope: string): number {
    return (this.filteredAttributesByScope[scope] ?? []).filter(attr => attr.kpi === true).length;
  }

  getScopeLabel(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }

  getScopeOriginalKeysLabel(scope: string): string {
    return getOriginalScopeKeys(scope).join(' + ');
  }

  getScopeDescription(scope: string): string {
    return this.catalogService.getScopeDescription(scope);
  }

  retry(): void {
    if (this.customerId) {
      this.loadCatalogForCustomer(this.customerId);
    } else if (this.catalogResponse) {
      this.errorMessage = null;
      this.processCatalogData(this.catalogResponse);
    }
  }

  getFilteredModelNames(): string[] {
    return Array.from(this.filteredModelAttributes.keys()).sort();
  }

  getModelAttributes(modelName: string): SignalAttribute[] {
    return this.filteredModelAttributes.get(modelName) || [];
  }

  // ==================== INLINE EDITING ====================

  private getAttributeKey(attr: SignalAttribute): string {
    return `${attr.name}|${attr.source}`;
  }

  private pendingKey(attr: SignalAttribute): string {
    return attr.uid ?? `${attr.name}_${attr.scope}`;
  }

  isEditing(attr: SignalAttribute): boolean {
    return this.editingKey === this.getAttributeKey(attr);
  }

  startEdit(attr: SignalAttribute): void {
    console.log('✏️ [SignalsCatalog20] Start editing:', attr.name);
    this.editingKey = this.getAttributeKey(attr);

    this.editingLabelField = new MultiLangField('name');
    this.editingLabelField.defaultPlaceholder = attr.label;
    setMultiLanguageField(this.editingLabelField);

    if (attr.i18nLabel && this.editingLabelField) {
      Object.entries(attr.i18nLabel).forEach(([langKey, labelValue]) => {
        const lang = langKey.split('-')[0];
        const existingLabel = this.editingLabelField!.labels.find(l => l.lang === lang);
        if (existingLabel) {
          existingLabel.value = labelValue;
        }
      });
      console.log('✏️ [SignalsCatalog20] Pre-popolate traduzioni esistenti:', attr.i18nLabel);
    }

    this.logger.debug('signals-catalog20', `Avviato editing per: ${attr.name}`);
  }

  cancelEdit(): void {
    this.editingKey = null;
    this.editingLabelField = null;
  }

  saveEdit(attr: SignalAttribute): void {
    if (!this.editingLabelField) return;

    this.isSaving = true;
    this.logger.debug('signals-catalog20', `Salvataggio locale modifiche per: ${attr.name}`);

    const i18nLabel: { [key: string]: string } = {};
    this.editingLabelField.labels.forEach(labelItem => {
      if (labelItem.value && labelItem.value.trim() !== '') {
        const langKey = `${labelItem.lang}-${labelItem.lang.toUpperCase()}`;
        i18nLabel[langKey] = labelItem.value;
      }
    });

    console.log('📝 [SignalsCatalog20] i18nLabel costruito da multilang-input:', i18nLabel);
    attr.i18nLabel = i18nLabel;
    this.syncPendingChange(attr);

    this.logger.info('signals-catalog20', `Modifiche salvate localmente per: ${attr.name}`);
    this.isSaving = false;
    this.cancelEdit();
  }

  public getCatalogChanges(): any[] | null {
    if (!this.catalogResponse?.data?.length) return null;

    return this.catalogResponse.data.map(signal => {
      const key = signal.uid ?? `${signal.name}_${signal.scope}`;
      const pending = this.pendingChanges.get(key);
      return {
        ...signal,
        isNew: false,
        ...(pending ? {
          visible: pending.visible ?? signal.visible,
          kpi: pending.kpi ?? signal.kpi,
          i18nLabel: pending.i18nLabel ?? signal.i18nLabel,
        } : {}),
      };
    });
  }

  get hasPendingChanges(): boolean { return this.pendingChanges.size > 0; }

  public resetPendingChanges(): void {
    this.pendingChanges.clear();
    console.log('🗑️ [SignalsCatalog20] Modifiche pending resetate');
  }

  isModified(attr: SignalAttribute): boolean {
    return this.pendingChanges.has(this.pendingKey(attr));
  }

  getModifiedCountForScope(scope: string): number {
    let count = 0;
    this.pendingChanges.forEach((attr) => {
      if (normalizeScope(attr.scope || 'other') === scope) count++;
    });
    return count;
  }

  onLabelChange(field: MultiLangField): void {
    this.editingLabelField = field;
  }

  /**
   * Verifica se la query (già lowercase) corrisponde a una qualsiasi etichetta del segnale:
   * etichetta originale, etichetta visualizzata (lingua corrente) o una delle traduzioni i18n.
   * La colonna mostra getDisplayLabel(), quindi la ricerca deve coprire anche le etichette personalizzate.
   */
  private labelMatches(attr: SignalAttribute, query: string): boolean {
    if (!query) return true;
    const candidates = [attr.label, this.getDisplayLabel(attr), ...Object.values(attr.i18nLabel ?? {})];
    return candidates.some(l => !!l && l.toLowerCase().includes(query));
  }

  /**
   * Descrizione del segnale nella lingua corrente (chiave "it-IT", "en-EN"...), con fallback
   * su una chiave che inizi con la lingua e infine sulla prima descrizione disponibile.
   */
  getDisplayDescription(attr: SignalAttribute): string {
    const descriptions = attr.description;
    if (!descriptions) return '';
    const lang = (localStorage.getItem('language') || 'en').toLowerCase();
    const exact = descriptions[`${lang}-${lang.toUpperCase()}`];
    if (exact) return exact;
    const byPrefix = Object.keys(descriptions).find(k => k.toLowerCase().startsWith(lang));
    if (byPrefix && descriptions[byPrefix]) return descriptions[byPrefix];
    return Object.values(descriptions).find(v => !!v) ?? '';
  }

  getDisplayLabel(attr: SignalAttribute): string {
    if (attr.i18nLabel) {
      const currentLang = localStorage.getItem('language') || 'en';
      const currentLangKey = `${currentLang}-${currentLang.toUpperCase()}`;
      if (attr.i18nLabel[currentLangKey]) {
        return attr.i18nLabel[currentLangKey];
      }
    }
    return attr.label;
  }

  onVisibleChange(attr: SignalAttribute, isVisible: boolean): void {
    attr.visible = isVisible;
    this.syncPendingChange(attr);
  }

  onKpiChange(attr: SignalAttribute, isKpi: boolean): void {
    attr.kpi = isKpi;
    this.syncPendingChange(attr);
  }

  // ==================== SELEZIONA TUTTI (VISIBILE) PER SCOPE ====================

  /** Righe dello scope su cui agisce il "seleziona tutti": quelle filtrate e non in editing */
  private selectableAttributes(scope: string): SignalAttribute[] {
    return (this.filteredAttributesByScope[scope] ?? []).filter(attr => !this.isEditing(attr));
  }

  areAllVisible(scope: string): boolean {
    const attrs = this.selectableAttributes(scope);
    return attrs.length > 0 && attrs.every(attr => attr.visible !== false);
  }

  isVisibleIndeterminate(scope: string): boolean {
    const attrs = this.selectableAttributes(scope);
    const visible = attrs.filter(attr => attr.visible !== false).length;
    return visible > 0 && visible < attrs.length;
  }

  toggleAllVisible(scope: string, checked: boolean): void {
    this.selectableAttributes(scope).forEach(attr => {
      attr.visible = checked;
      this.syncPendingChange(attr);
    });
  }

  // ==================== TRACCIAMENTO MODIFICHE ====================

  /**
   * Aggiorna pendingChanges confrontando la riga con lo snapshot caricato dal backend:
   * se visibile, kpi ed etichette sono tornati ai valori salvati la riga non è più "modificata".
   */
  private syncPendingChange(attr: SignalAttribute): void {
    const key = this.pendingKey(attr);
    const original = this.originalAttributes.get(key);
    if (original && this.isSameAsOriginal(attr, original)) {
      this.pendingChanges.delete(key);
    } else {
      this.pendingChanges.set(key, { ...attr });
    }
  }

  private isSameAsOriginal(attr: SignalAttribute, original: SignalAttribute): boolean {
    const normLabels = (labels?: { [key: string]: string }) =>
      JSON.stringify(
        Object.entries(labels ?? {})
          .filter(([, v]) => !!v && v.trim() !== '')
          .sort(([a], [b]) => a.localeCompare(b))
      );
    return (attr.visible !== false) === (original.visible !== false)
      && (attr.kpi === true) === (original.kpi === true)
      && normLabels(attr.i18nLabel) === normLabels(original.i18nLabel);
  }
}
